import { useState, useEffect, useCallback } from 'react';
import { getValidToken } from '../utils/auth';
import TradingDatePicker from './TradingDatePicker';
import { etTodayStr } from '../utils/tradingCalendar';

const API_URL = process.env.REACT_APP_API_URL || '';

const STATUS_COLOR = { Active: '#60a5fa', Success: '#22c55e', Failed: '#ef4444' };

// Three short-side strategy scanners (backend: ws/strategy_engine.go).
// Each widget = live candidates (today only) + tracked signals for the
// selected day. All signals are SHORTS: Success = TP touched, Failed = SL.
const STRATEGIES = [
  {
    key: 'gapfade',
    title: 'Gap & Fade',
    desc: 'Gap ≥30% on ≥1M premarket vol → short the first lower high that closes below VWAP · SL = HOD · TP = 2R',
    candCols: [
      { key: 'symbol', label: 'Ticker', fmt: (c) => c.symbol },
      { key: 'price', label: 'Price', fmt: (c) => `$${c.price.toFixed(2)}` },
      { key: 'gap_pct', label: 'Gap %', fmt: (c) => `${c.gap_pct >= 0 ? '+' : ''}${c.gap_pct.toFixed(0)}%` },
      { key: 'pre_vol', label: 'PreVol', fmt: (c) => `${(c.pre_vol / 1e6).toFixed(1)}M` },
      { key: 'vwap_dist', label: 'vs VWAP', fmt: (c) => `${c.vwap_dist >= 0 ? '+' : ''}${c.vwap_dist.toFixed(1)}%` },
      { key: 'hod', label: 'HOD', fmt: (c) => `$${c.hod.toFixed(2)}` },
    ],
  },
  {
    key: 'vwaprej',
    title: 'VWAP Rejection (Backside)',
    desc: 'Ran ≥20% intraday, below VWAP + 9EMA ≥30 min → short the red rejection candle at VWAP · SL = 1% over VWAP · TP = 2R',
    candCols: [
      { key: 'symbol', label: 'Ticker', fmt: (c) => c.symbol },
      { key: 'price', label: 'Price', fmt: (c) => `$${c.price.toFixed(2)}` },
      { key: 'run_pct', label: 'Run %', fmt: (c) => `+${c.run_pct.toFixed(0)}%` },
      { key: 'vwap_dist', label: 'vs VWAP', fmt: (c) => `${c.vwap_dist >= 0 ? '+' : ''}${c.vwap_dist.toFixed(1)}%` },
      { key: 'mins_below', label: 'Below', fmt: (c) => (c.mins_below > 0 ? `${c.mins_below}m` : '—') },
    ],
  },
  {
    key: 'firstred',
    title: 'First Red Day',
    desc: '2+ green days w/ expanding range → after 14:00, short the break of the 12:00–14:00 low (below open & VWAP) · SL = cons. high · TP = 2R',
    candCols: [
      { key: 'symbol', label: 'Ticker', fmt: (c) => c.symbol },
      { key: 'price', label: 'Price', fmt: (c) => `$${c.price.toFixed(2)}` },
      { key: 'vs_open_pct', label: 'vs PrevCls', fmt: (c) => `${c.vs_open_pct >= 0 ? '+' : ''}${c.vs_open_pct.toFixed(1)}%` },
      { key: 'vwap_dist', label: 'vs VWAP', fmt: (c) => `${c.vwap_dist >= 0 ? '+' : ''}${c.vwap_dist.toFixed(1)}%` },
    ],
  },
];

function formatTime(unixMs) {
  if (!unixMs) return '—';
  return new Date(unixMs).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York',
  });
}

function openTradingView(symbol) {
  window.open(`https://www.tradingview.com/chart/?symbol=${symbol}&interval=1`, '_blank', 'noopener,noreferrer');
}

function StrategyWidget({ cfg, candidates, signals, isToday }) {
  const counts = { Active: 0, Success: 0, Failed: 0 };
  for (const s of signals) if (counts[s.status] != null) counts[s.status]++;

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          {cfg.title}
        </span>
        <span className="text-xs text-gray-500">SHORT</span>
        <div className="flex-1" />
        {Object.entries(counts).map(([st, n]) => (
          <span key={st} className="text-xs font-semibold" style={{ color: STATUS_COLOR[st] }}>{st} {n}</span>
        ))}
      </div>
      <div className="text-xs text-gray-500 mb-3">{cfg.desc}</div>

      {/* Live candidates (today only) */}
      {isToday && (
        <div className="overflow-x-auto mb-3">
          <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
                {cfg.candCols.map((col) => (
                  <th key={col.key} className="text-left py-2 pr-3 font-medium">{col.label}</th>
                ))}
                <th className="text-left py-2 pr-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {candidates.length === 0 ? (
                <tr><td colSpan={cfg.candCols.length + 1} className="py-4 text-center text-gray-600">
                  No candidates yet
                </td></tr>
              ) : candidates.map((c) => (
                <tr key={c.symbol} onClick={() => openTradingView(c.symbol)}
                  className="border-b cursor-pointer hover:bg-gray-800/50"
                  style={{ borderColor: '#1f2937' }}>
                  {cfg.candCols.map((col, i) => (
                    <td key={col.key} className={`py-1.5 pr-3 ${i === 0 ? 'font-semibold text-gray-200' : 'text-gray-400 font-mono'}`}>
                      {col.fmt(c)}
                    </td>
                  ))}
                  <td className="py-1.5 pr-3">
                    {c.signaled && <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                      style={{ backgroundColor: '#ef444422', color: '#ef4444' }}>FIRED</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tracked signals */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
              {['Ticker', 'Entry', 'SL', 'TP', 'H', 'L', 'Note', 'Time', 'Status'].map((h) => (
                <th key={h} className={`py-2 pr-3 font-medium ${h === 'Ticker' || h === 'Note' ? 'text-left' : 'text-right'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {signals.length === 0 ? (
              <tr><td colSpan={9} className="py-4 text-center text-gray-600">No signals</td></tr>
            ) : signals.map((s, i) => (
              <tr key={`${s.symbol}-${s.added_at}-${i}`} onClick={() => openTradingView(s.symbol)}
                className="border-b cursor-pointer hover:bg-gray-800/50" style={{ borderColor: '#1f2937' }}>
                <td className="py-1.5 pr-3 font-semibold text-gray-200">{s.symbol}</td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-300">${s.entry.toFixed(2)}</td>
                <td className="py-1.5 pr-3 text-right font-mono" style={{ color: '#ef4444' }}>${s.sl.toFixed(2)}</td>
                <td className="py-1.5 pr-3 text-right font-mono" style={{ color: '#22c55e' }}>${s.tp.toFixed(2)}</td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-400">${s.high.toFixed(2)}</td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-400">${s.low.toFixed(2)}</td>
                <td className="py-1.5 pr-3 text-gray-500">{s.note}</td>
                <td className="py-1.5 pr-3 text-right text-gray-400">{formatTime(s.added_at)}</td>
                <td className="py-1.5 pr-3 text-right">
                  <span className="font-semibold px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: (STATUS_COLOR[s.status] || '#6b7280') + '22', color: STATUS_COLOR[s.status] || '#6b7280' }}>
                    {s.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ShortStrategies() {
  const [date, setDate] = useState(() => etTodayStr());
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (d) => {
    try {
      const token = await getValidToken();
      const res = await fetch(`${API_URL}/api/strategies?date=${d}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    fetchData(date);
    const timer = setInterval(() => fetchData(date), 10000);
    return () => clearInterval(timer);
  }, [date, fetchData]);

  const isToday = date === etTodayStr();

  return (
    <div>
      <div className="flex items-center gap-3 mb-4 mt-2 flex-wrap">
        <h2 className="text-lg font-bold text-gray-900">Short Strategies</h2>
        <span className="text-xs text-gray-500">Gap & Fade · VWAP Rejection · First Red Day — signals kept 30 days</span>
        <div className="flex-1" />
        <TradingDatePicker value={date} onChange={setDate} />
      </div>
      {error && <div className="text-red-400 text-sm mb-3">Failed to load: {error}</div>}
      {STRATEGIES.map((cfg) => (
        <StrategyWidget
          key={cfg.key}
          cfg={cfg}
          candidates={data?.candidates?.[cfg.key] || []}
          signals={data?.signals?.[cfg.key] || []}
          isToday={isToday}
        />
      ))}
    </div>
  );
}
